import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Check, AlertTriangle, ArrowRight, CheckCircle2, Download } from "lucide-react";
import axios from "axios";
import { Btn, Card, Spinner, Textarea, Input, Label } from "@/components/ui-kit";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import TaskDescription from "@/components/TaskDescription";
import { API } from "@/lib/api";
import { formatCostDisplay, formatDateUK, formatGBP } from "@/lib/format";

const publicApi = axios.create({ baseURL: API });

const QuotePublicPage = () => {
  const { token } = useParams();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [comment, setComment] = useState("");
  const [confirmedBy, setConfirmedBy] = useState("");
  const [email, setEmail] = useState("");
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const [submitting, setSubmitting] = useState(false);
  const [thanksOpen, setThanksOpen] = useState(false);
  const [lastSubmittedAt, setLastSubmittedAt] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await publicApi.get(`/public/quotes/${token}`);
        if (cancelled) return;
        setQuote(data);
        // Pre-tick anything already chosen so a returning client sees their last submission
        const pre = new Set((data.tasks || []).filter((t) => t.client_chosen).map((t) => t.id));
        setSelectedIds(pre);
        setComment(data.client_selection_comment || "");
        setConfirmedBy(data.client_confirmed_by || "");
        setEmail(data.client_confirmed_email || "");
        setLastSubmittedAt(data.client_selected_at || "");
      } catch (e) {
        setError(e?.response?.data?.detail || "Could not load this quote.");
      } finally {
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const toggleTask = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      await publicApi.post(`/public/quotes/${token}/select`, {
        selected_task_ids: Array.from(selectedIds),
        comment: comment.trim(),
        confirmed_by: confirmedBy.trim(),
        email: email.trim(),
      });
      // Reflect committed selection locally
      setQuote((q) => ({
        ...q,
        tasks: (q.tasks || []).map((t) => ({ ...t, client_chosen: selectedIds.has(t.id) })),
      }));
      setLastSubmittedAt(new Date().toISOString());
      setThanksOpen(true);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not submit your selection.");
    } finally {
      setSubmitting(false);
    }
  };

  const total = useMemo(() => {
    if (!quote) return 0;
    return (quote.tasks || [])
      .filter((t) => selectedIds.has(t.id))
      .reduce((sum, t) => sum + (t.cost_type === "range" ? Number(t.cost_max || 0) : Number(t.cost || 0)), 0);
  }, [quote, selectedIds]);

  const hasRange = useMemo(() => {
    if (!quote) return false;
    return (quote.tasks || []).some((t) => selectedIds.has(t.id) && t.cost_type === "range");
  }, [quote, selectedIds]);

  if (loading || !quote) {
    if (error) {
      return (
        <div className="min-h-screen grid place-items-center bg-zinc-50">
          <Card className="p-10 text-center max-w-md">
            <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
            <h1 className="font-display text-2xl font-bold mb-2">Quote not found</h1>
            <p className="text-sm text-zinc-500">{error}</p>
          </Card>
        </div>
      );
    }
    return <div className="min-h-screen grid place-items-center"><Spinner /></div>;
  }

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="quote-public-page">
      {/* Header */}
      <header className="bg-white border-b border-zinc-200 sticky top-0 z-30">
        <div className="px-4 sm:px-6 py-5 flex items-center justify-between gap-4 flex-wrap max-w-[1000px] mx-auto">
          <div className="flex items-center gap-5 min-w-0">
            <img src="/kerboom-logo.png" alt="Kerboom" className="h-12 sm:h-14 w-auto shrink-0" data-testid="quote-public-logo" />
            <div className="min-w-0 border-l border-zinc-200 pl-5">
              <h1 className="font-display font-bold text-zinc-950 text-2xl sm:text-3xl leading-tight tracking-tight">Your Kerboom Quote</h1>
              <p className="text-xs sm:text-sm text-zinc-500 mt-0.5 truncate">Sent by {quote.designer_name} · Kerboom Design</p>
            </div>
          </div>
        </div>
      </header>

      <main className="px-4 sm:px-6 py-8 max-w-[1000px] mx-auto">
        {/* Title block */}
        <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-widest text-zinc-500 font-semibold">{quote.client_name}</p>
            <h2 className="font-display text-3xl font-bold text-zinc-950 mt-1" data-testid="quote-public-name">{quote.name}</h2>
            <p className="text-sm text-zinc-500 mt-1">
              Quote Q{quote.quote_number}{quote.date ? ` · dated ${formatDateUK(quote.date)}` : ""}
            </p>
          </div>
          <a
            href={`${API}/public/quotes/${token}/pdf`}
            download={`${quote.client_name || "Client"} Quotation Q${quote.quote_number}.pdf`}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold bg-zinc-900 text-white hover:bg-zinc-800 shadow-sm transition-colors shrink-0"
            data-testid="quote-public-download-pdf"
            title="Download a clean printable copy of this quote"
          >
            <Download className="w-4 h-4" />
            Download PDF
          </a>
        </div>

        {/* Instructions */}
        <Card className="p-6 mb-6 bg-amber-50/60 border-amber-200">
          <h3 className="font-display text-lg font-bold text-zinc-950 mb-1">Choose the options you'd like to go ahead with</h3>
          <p className="text-sm text-zinc-700">Tick the boxes next to each option you want to confirm. You can pick one, several, or all of them. Add a comment at the bottom if you have any questions, and hit <strong>Send my selections</strong> — that's it. Your selections will be recorded for the studio.</p>
        </Card>

        {/* Options */}
        <div className="space-y-3 mb-8" data-testid="quote-public-tasks">
          {(quote.tasks || []).map((t) => {
            const checked = selectedIds.has(t.id);
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => toggleTask(t.id)}
                className={`w-full text-left rounded-2xl border-2 p-5 transition-all flex items-start gap-4 ${
                  checked
                    ? "border-emerald-500 bg-emerald-50/60 shadow-md ring-2 ring-emerald-200"
                    : "border-zinc-200 bg-white hover:border-zinc-400 hover:shadow-sm"
                }`}
                data-testid={`quote-public-task-${t.id}`}
              >
                <span
                  className={`mt-1 w-6 h-6 rounded-md border-2 grid place-items-center shrink-0 ${
                    checked
                      ? "bg-emerald-500 border-emerald-500 text-white"
                      : "border-zinc-300 bg-white"
                  }`}
                  aria-hidden
                >
                  {checked && <Check className="w-4 h-4" strokeWidth={3} />}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-4 flex-wrap">
                    <p className="font-display text-lg font-bold text-zinc-950">{t.name}</p>
                    <p className="font-display text-lg font-bold text-zinc-950 tabular-nums">{formatCostDisplay(t)}</p>
                  </div>
                  {t.description && (
                    <TaskDescription text={t.description} className="text-sm text-zinc-600" bulletClassName="text-sm text-zinc-600 mt-1" />
                  )}
                  {checked && (
                    <p className="text-xs font-semibold text-emerald-700 mt-2 inline-flex items-center gap-1">
                      <Check className="w-3 h-3" />Selected
                    </p>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        {/* Running total */}
        {selectedIds.size > 0 && (
          <div className="rounded-2xl bg-zinc-950 text-white px-6 py-4 mb-6 flex items-center justify-between flex-wrap gap-3" data-testid="quote-public-total">
            <div>
              <p className="text-xs uppercase tracking-widest text-zinc-400 font-semibold">Your selected total</p>
              <p className="text-[11px] text-zinc-500 mt-0.5">{selectedIds.size} option{selectedIds.size !== 1 ? "s" : ""} chosen · excludes VAT</p>
            </div>
            <p className="font-display text-2xl font-bold tabular-nums">
              {hasRange ? `up to ${formatGBP(total)}` : formatGBP(total)}
            </p>
          </div>
        )}

        {/* Your name */}
        <div className="mb-4">
          <label className="block text-sm font-semibold text-zinc-900 mb-2" htmlFor="quote-public-name-input">
            Your name <span className="text-red-600">*</span>
            <span className="text-zinc-400 font-normal ml-1">(so the studio knows who confirmed)</span>
          </label>
          <Input
            id="quote-public-name-input"
            type="text"
            value={confirmedBy}
            onChange={(e) => setConfirmedBy(e.target.value)}
            placeholder="e.g. Jane Smith, Bowood Hotel"
            data-testid="quote-public-confirmed-by"
            maxLength={120}
            required
          />
        </div>

        {/* Your email */}
        <div className="mb-4">
          <label className="block text-sm font-semibold text-zinc-900 mb-2" htmlFor="quote-public-email-input">
            Your email <span className="text-red-600">*</span>
            <span className="text-zinc-400 font-normal ml-1">(so we can confirm your selections)</span>
          </label>
          <Input
            id="quote-public-email-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="e.g. jane@bowoodhotel.co.uk"
            data-testid="quote-public-email"
            maxLength={200}
            required
          />
        </div>

        {/* Comment box */}
        <div className="mb-6">
          <label className="block text-sm font-semibold text-zinc-900 mb-2">
            Any comments or questions? <span className="text-zinc-400 font-normal">(optional)</span>
          </label>
          <Textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={4}
            placeholder="e.g. Could you confirm the turnaround time on option 2? Happy to proceed otherwise."
            data-testid="quote-public-comment"
            maxLength={2000}
          />
        </div>

        {/* Already submitted note */}
        {lastSubmittedAt && (
          <p className="text-xs text-zinc-500 mb-4" data-testid="quote-public-last-submitted">
            Last sent {new Date(lastSubmittedAt).toLocaleString("en-GB")} — you can change your mind and resubmit.
          </p>
        )}

        {/* Submit */}
        <div className="flex flex-col items-end gap-2">
          {selectedIds.size > 0 && !confirmedBy.trim() && (
            <p className="text-xs text-amber-700" data-testid="quote-public-name-required-hint">
              Please add your name above so we know who confirmed.
            </p>
          )}
          {selectedIds.size > 0 && confirmedBy.trim() && !emailValid && (
            <p className="text-xs text-amber-700" data-testid="quote-public-email-required-hint">
              Please add a valid email address so we can confirm your selections.
            </p>
          )}
          <button
            type="button"
            onClick={submit}
            disabled={submitting || selectedIds.size === 0 || !confirmedBy.trim() || !emailValid}
            className="inline-flex items-center gap-2 px-7 py-3 rounded-full text-base font-semibold bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
            data-testid="quote-public-submit"
          >
            {submitting ? "Sending…" : "Send my selections"}
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>

        {error && quote && (
          <p className="text-sm text-red-700 mt-3 text-right">{error}</p>
        )}

        <p className="text-center text-xs text-zinc-500 mt-12 pt-6 border-t border-zinc-200">
          © Kerboom Design — bespoke design &amp; print for businesses you'd remember.
        </p>
      </main>

      <Dialog open={thanksOpen} onOpenChange={setThanksOpen}>
        <DialogContent className="sm:max-w-md" data-testid="quote-public-thanks">
          <DialogHeader>
            <div className="mx-auto w-14 h-14 rounded-full bg-emerald-100 grid place-items-center mb-3">
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            </div>
            <DialogTitle className="text-center font-display text-2xl">Thanks — selections sent</DialogTitle>
            <DialogDescription className="text-center text-sm text-zinc-600 pt-2">
              Your selections have been recorded. The studio will come back to you with next steps shortly.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center pt-2">
            <Btn onClick={() => setThanksOpen(false)} data-testid="quote-public-thanks-close">Close</Btn>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default QuotePublicPage;
